import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@vd/ui';
import styles from './JiraExportHelp.module.css';

/**
 * How sending to Jira works: one text, shown in two places. "Learn more about
 * Jira export" opens it in a modal on the company Jira tab and the project
 * mapping tab (TOADO-709), and below the Team plan it is the page itself, with
 * an upgrade (TOADO-713).
 *
 * Every sentence rests on code, named beside it, so a change there is a change
 * here: the routes in `apps/api/src/routes/jira.ts`, the issue the worker
 * builds in `apps/api/src/jira/export.ts`, `exportImages.ts` and `adf.ts`, the
 * menus in `features/jira/JiraMenuItem.tsx`, and the board's sending in
 * `routes/board/ProjectViewsPage.tsx`.
 */
export function JiraExportGuide() {
  return (
    <>
      {/* Nothing in the API sends a ticket unless someone asks: POST
          /tickets/:id/jira-export and /projects/:id/jira-export only. */}
      <section>
        <h3>You choose what goes</h3>
        <p>Nothing is sent to Jira on its own. Connecting Jira and mapping a project make sending possible; a ticket goes to Jira when someone on the team sends it.</p>
      </section>
      {/* Connect and the site picker are owner-only (requireCompanyOwner404); so is the mapping (requireProjectOwner404). */}
      <section>
        <h3>Setting it up</h3>
        <ol>
          <li>A workspace owner connects Jira, under Company settings, Jira, and chooses the Jira site when their Atlassian account sees more than one.</li>
          <li>For each project, an owner maps it to a Jira project, and each kind of ticket to a Jira issue type.</li>
        </ol>
      </section>
      {/* The ticket page's and the card's ⋯ menus (JiraMenuItem), and the bulk bar. Viewers are refused (requireWriteRole). */}
      <section>
        <h3>Sending tickets</h3>
        <ul>
          <li>From a ticket's page or a card on the board, in its <strong>⋯</strong> menu: <strong>Send to Jira</strong>.</li>
          <li>Several at once: select the cards, then <strong>Send to Jira</strong> in the bar that appears.</li>
        </ul>
        <p className={styles.note}>Members and owners can send. Viewers can see what was sent but not send.</p>
      </section>
      {/* buildJiraDescription (adf.ts), ticketImages and drawForJira (exportImages.ts), labels in export.ts. */}
      <section>
        <h3>What goes to Jira</h3>
        <ul>
          <li>The title, as the issue's summary.</li>
          <li>The description, with the page it was captured on, the screen size, and a link back to the ticket in Toado.</li>
          <li>Every picture on the ticket, once each, drawn with its annotations, so redactions and blurs stay in place.</li>
          {/* jira/video.ts (TOADO-704). */}
          <li>A walkthrough's video, when it fits the Jira site's attachment size limit. When it does not, a comment on the issue says so and links to it in Toado.</li>
          <li>The issue type mapped for the ticket's kind, your default labels, and a <code>toado-</code> label naming the ticket.</li>
        </ul>
        <p className={styles.note}>Console and network logs stay in Toado. The link back opens them for people who can see the ticket in Toado.</p>
      </section>
      {/* The chip and menu (TOADO-708), unlink (jira/unlink.ts), the deleted-issue check (TOADO-712), the earlier-version stop (TOADO-716). */}
      <section>
        <h3>After sending</h3>
        <ul>
          <li>The card shows the Jira issue key. Click it to open the issue.</li>
          <li>Each ticket is sent once: sending it again leaves it alone. If a send fails, the ticket says why and you can try again.</li>
          <li>Nothing comes back from Jira: a status, comment or assignee changed there leaves the ticket in Toado as it was.</li>
          <li><strong>Unlink</strong>, in the <strong>⋯</strong> menu, forgets the issue without changing Jira. Sending again links it back while it is still in Jira, or makes a new one.</li>
          <li>When an issue is deleted in Jira, Toado unlinks its ticket once Jira tells it so. Where Jira cannot tell Toado, the ticket's page says the issue is missing while its project is mapped, and you can unlink it.</li>
          <li>Toado never deletes an issue or an attachment in Jira. If a picture has changed since it was sent, sending to the same issue again stops and names the earlier file to remove there first.</li>
          <li>Removing a mapping, or disconnecting Jira, leaves issues already in Jira where they are.</li>
        </ul>
      </section>
    </>
  );
}

/** "Learn more about Jira export", and the modal it opens. */
export function JiraExportHelp() {
  const [open, setOpen] = useState(false);
  const title = useId();
  return (
    <>
      <button type="button" className={styles.trigger} onClick={() => setOpen(true)}>Learn more about Jira export</button>
      <Modal open={open} onClose={() => setOpen(false)} width={560} labelledBy={title}>
        <ModalHeader id={title}>How Jira export works</ModalHeader>
        <ModalBody className={styles.body}>
          <JiraExportGuide />
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={() => setOpen(false)}>Got it</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

/**
 * What the Jira pages show on the Free and Personal plans (TOADO-713): what
 * Jira export does and how, and the way to the Team plan. The server refuses
 * connecting, mapping and sending below Team; links already made stay usable.
 */
export function JiraNeedsTeam({ companyId }: { companyId: string }) {
  return (
    <div className={styles.upgrade}>
      <div className={styles.upgradeHead}>
        <h2>Send tickets to Jira, on the Team plan</h2>
        <p>Jira export is part of the Team, Business and Enterprise plans. Upgrade to connect your Jira site and send tickets from Toado, with their pictures, video and a link back.</p>
        <div>
          <Link className={styles.upgradeButton} to={`/company/settings/billing?workspace=${encodeURIComponent(companyId)}`}>Upgrade to Team</Link>
        </div>
      </div>
      <div className={styles.guide}>
        <JiraExportGuide />
      </div>
    </div>
  );
}
